import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  deleteDoc,
  writeBatch
} from 'firebase/firestore';
import { ref, deleteObject, listAll } from 'firebase/storage';
import { db, storage } from '../firebase';

/**
 * Service for deleting all user data (GDPR Right to Erasure)
 */
const UserDeletionService = {
  /**
   * Delete all user data from Firestore collections
   * @param {string} userId - User ID to delete data for
   * @returns {Promise<Object>} - Summary of deleted data
   */
  async deleteAllUserData(userId) {
    const summary = {
      items: 0,
      wasteLogs: 0,
      consumption: 0,
      notifications: 0,
      posts: 0,
      comments: 0,
      userProfile: false,
      errors: []
    };

    try {
      // Delete items
      try {
        const itemsQuery = query(
          collection(db, 'items'),
          where('userId', '==', userId)
        );
        const itemsSnapshot = await getDocs(itemsQuery);
        const itemsBatch = writeBatch(db);
        itemsSnapshot.docs.forEach((docSnap) => {
          itemsBatch.delete(docSnap.ref);
        });
        await itemsBatch.commit();
        summary.items = itemsSnapshot.size;
      } catch (error) {
        console.error('Error deleting items:', error);
        summary.errors.push({ collection: 'items', error: error.message });
      }

      // Delete waste logs
      try {
        const wasteQuery = query(
          collection(db, 'wasteLogs'),
          where('userId', '==', userId)
        );
        const wasteSnapshot = await getDocs(wasteQuery);
        const wasteBatch = writeBatch(db);
        wasteSnapshot.docs.forEach((docSnap) => {
          wasteBatch.delete(docSnap.ref);
        });
        await wasteBatch.commit();
        summary.wasteLogs = wasteSnapshot.size;
      } catch (error) {
        console.error('Error deleting waste logs:', error);
        summary.errors.push({ collection: 'wasteLogs', error: error.message });
      }

      // Delete consumption logs
      try {
        const consumptionQuery = query(
          collection(db, 'consumption'),
          where('userId', '==', userId)
        );
        const consumptionSnapshot = await getDocs(consumptionQuery);
        const consumptionBatch = writeBatch(db);
        consumptionSnapshot.docs.forEach((docSnap) => {
          consumptionBatch.delete(docSnap.ref);
        });
        await consumptionBatch.commit();
        summary.consumption = consumptionSnapshot.size;
      } catch (error) {
        console.error('Error deleting consumption logs:', error);
        summary.errors.push({ collection: 'consumption', error: error.message });
      }

      // Delete notifications
      try {
        const notificationsQuery = query(
          collection(db, 'notifications'),
          where('userId', '==', userId)
        );
        const notificationsSnapshot = await getDocs(notificationsQuery);
        const notificationsBatch = writeBatch(db);
        notificationsSnapshot.docs.forEach((docSnap) => {
          notificationsBatch.delete(docSnap.ref);
        });
        await notificationsBatch.commit();
        summary.notifications = notificationsSnapshot.size;
      } catch (error) {
        console.error('Error deleting notifications:', error);
        summary.errors.push({ collection: 'notifications', error: error.message });
      }

      // Delete user's posts
      try {
        const postsQuery = query(
          collection(db, 'posts'),
          where('authorId', '==', userId)
        );
        const postsSnapshot = await getDocs(postsQuery);
        const postsBatch = writeBatch(db);
        postsSnapshot.docs.forEach((docSnap) => {
          postsBatch.delete(docSnap.ref);
        });
        await postsBatch.commit();
        summary.posts = postsSnapshot.size;
      } catch (error) {
        console.error('Error deleting posts:', error);
        summary.errors.push({ collection: 'posts', error: error.message });
      }

      // Delete user's comments
      try {
        const commentsQuery = query(
          collection(db, 'comments'),
          where('authorId', '==', userId)
        );
        const commentsSnapshot = await getDocs(commentsQuery);
        const commentsBatch = writeBatch(db);
        commentsSnapshot.docs.forEach((docSnap) => {
          commentsBatch.delete(docSnap.ref);
        });
        await commentsBatch.commit();
        summary.comments = commentsSnapshot.size;
      } catch (error) {
        console.error('Error deleting comments:', error);
        summary.errors.push({ collection: 'comments', error: error.message });
      }

      // Delete user document from users collection
      try {
        const userDocRef = doc(db, 'users', userId);
        await deleteDoc(userDocRef);
        summary.userProfile = true;
      } catch (error) {
        console.error('Error deleting user profile:', error);
        summary.errors.push({ collection: 'users', error: error.message });
      }

      // Delete user document from userProfiles collection (if exists)
      try {
        const userProfileDocRef = doc(db, 'userProfiles', userId);
        const userProfileDocSnap = await getDoc(userProfileDocRef);
        if (userProfileDocSnap.exists()) {
          await deleteDoc(userProfileDocRef);
        }
      } catch (error) {
        // Ignore if collection doesn't exist or document doesn't exist
        console.log('UserProfiles document may not exist:', error.message);
      }

      return summary;
    } catch (error) {
      console.error('Error in deleteAllUserData:', error);
      throw error;
    }
  },

  /**
   * Delete all user files from Firebase Storage
   * @param {string} userId - User ID to delete files for
   * @returns {Promise<number>} - Number of files deleted
   */
  async deleteUserFiles(userId) {
    let deletedCount = 0;
    try {
      // Delete avatar objects (current + legacy user_{uid} filename)
      for (const path of [`avatars/${userId}`, `avatars/user_${userId}`]) {
        try {
          await deleteObject(ref(storage, path));
          deletedCount += 1;
        } catch (error) {
          if (error.code !== 'storage/object-not-found') {
            console.error('Error deleting avatar file:', path, error);
          }
        }
      }

      // Delete this user's post images under posts/{uid}/
      try {
        const postsFolder = await listAll(ref(storage, `posts/${userId}`));
        await Promise.all(postsFolder.items.map((itemRef) => deleteObject(itemRef)));
        deletedCount += postsFolder.items.length;
      } catch (error) {
        if (error.code !== 'storage/object-not-found') {
          console.error('Error deleting post images:', error);
        }
      }
    } catch (error) {
      console.error('Error in deleteUserFiles:', error);
      // Don't throw - file deletion is not critical
    }
    return deletedCount;
  },

  /**
   * Delete all user data (Firestore + Storage)
   * @param {string} userId - User ID to delete data for
   * @returns {Promise<Object>} - Complete deletion summary
   */
  async deleteAllData(userId) {
    const [firestoreSummary, filesDeleted] = await Promise.all([
      this.deleteAllUserData(userId),
      this.deleteUserFiles(userId)
    ]);

    return {
      ...firestoreSummary,
      filesDeleted
    };
  }
};

export default UserDeletionService;

